import * as React from 'react';
import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Box from '@mui/material/Box';
import Drawer from '@mui/material/Drawer';
import List from '@mui/material/List';
import Divider from '@mui/material/Divider';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Collapse from '@mui/material/Collapse';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import IconButton from '@mui/material/IconButton';
import logo from '../../Assets/Logos/logo-desk.svg';
import okoLogo from '../../Assets/Logos/OKO_New_Logo.svg';
import { AccessControlLeftBart } from './access_control_left_bar_path.component';
import { PartnerNetworkLeftBarPath } from './partner_network_left_bar_path.component';
import { NetworkMonitoringLeftBarPath } from './network_monitoring_left_bar_path.component';
import { InvoiceManagementLeftBarPath } from './invoice_management_left_bar_path.component';
import homeIcon from '../../Assets/AlertIcon/home_icon.svg';
import inventory from '../../Assets/AlertIcon/inventoryLeftBar.svg';
import circleBackArrow from '../../Assets/AlertIcon/circleBackArrow.svg';
import ticketIcon from '../../Assets/AlertIcon/ticket.svg';
import { getPermissions } from '../../common/functions/get_permissions_from_storage.function';
import {
  hasAnyPermission,
  hasNetworkMonitoringPermissions,
  getAvailableNetworkMonitoringPermissions,
  USER_MANAGEMENT_PERMISSIONS,
  PARTNER_NETWORK_PERMISSIONS,
  KMZ_PERMISSIONS,
  RING_PLANNING_PERMISSIONS,
  FIBER_POP_LIST_PERMISSIONS,
  hasApprovalPersonsSystemInvoiceManagementAccess,
  hasLoginLockoutManagementAccess,
} from '../../common/functions/permission_utils.function';

export default function TemporaryDrawer(props) {
  const { open, toggleDrawer } = props;
  const navigate = useNavigate();
  const location = useLocation();
  const environment = process.env.REACT_APP_ENVIRONMENT;

  // Get user permissions
  const permissions = getPermissions();
  const permissionNames = Array.isArray(permissions)
    ? permissions.map((permission) => permission.name)
    : [];

  const [expandedMenus, setExpandedMenus] = useState({
    'Network Monitoring': false, // Changed from true to false
    'Network Planning': false,
    'Inventory Management': false,
    'Invoice Management': false,
  });

  // Function to get current module and sub-module based on path
  const getCurrentModule = () => {
    const path = location.pathname;
    const selectedTab = sessionStorage.getItem('selectedTab');

    if (path.includes('/userManagement')) {
      return { module: 'Access Control', subModule: null };
    } else if (path.includes('/fiberSite') || path.includes('/fiberSitePlanning')) {
      return { module: 'Inventory Management', subModule: 'FTTS Site List' };
    } else if (path.includes('/randb') || path.includes('/ranDb')) {
      return { module: 'Inventory Management', subModule: 'RAN DB' };
    } else if (path.includes('/tenantManagement')) {
      return { module: 'Access Control', subModule: null };
    } else if (path.includes('/rov')) {
      return { module: 'Home', subModule: null };
    } else if (path.includes('/networkAlarms') || path.includes('/network_alarms')) {
      return { module: 'Network Monitoring', subModule: 'Network Alarms' };
    } else if (path.includes('/fibers')) {
      // Map selectedTab to the correct sub-module name
      if (selectedTab === 'ftts') {
        return { module: 'Network Monitoring', subModule: 'FTTS' };
      } else if (selectedTab === 'mpbn') {
        return { module: 'Network Monitoring', subModule: 'MBPN' };
      } else if (selectedTab === 'otn') {
        return { module: 'Network Monitoring', subModule: 'OTN' };
      } else if (selectedTab === 'ipran') {
        return { module: 'Network Monitoring', subModule: 'IPRAN' };
      } else {
        return { module: 'Network Monitoring', subModule: null };
      }
    } else if (path.includes('/networkLayer')) {
      return { module: 'Inventory Management', subModule: 'Network Layer Visualization' };
    } else if (path.includes('/inventory')) {
      return { module: 'Inventory Management', subModule: 'OFC Inventory' };
    } else if (path.includes('/leasedFiber')) {
      return { module: 'Inventory Management', subModule: 'Leased Fiber Pair' };
    } else if (path.includes('/networkPlanning/fiberPopList')) {
      return { module: 'Inventory Management', subModule: 'Fiber Pop List' };
    } else if (path.includes('/networkPlanning/kmzPlanning')) {
      return { module: 'Network Planning', subModule: 'KMZ' };
    } else if (path.includes('/networkPlanning/ringPlanning')) {
      return { module: 'Network Planning', subModule: 'Ring Planning' };
    } else if (path.includes('/kmzMap') || path.includes('/kmz')) {
      return { module: 'Network Planning', subModule: 'Partner Network' };
    } else if (path.includes('/purchaseOrderManagement')) {
      return { module: 'Invoice Management', subModule: 'Purchase Order Management' };
    } else if (path.includes('/poWorkflowManagement')) {
      return { module: 'Invoice Management', subModule: 'Vendor Invoice Management' };
    } else if (path.includes('/invoiceAttachmentManagement')) {
      return { module: 'Invoice Management', subModule: 'Invoice Attachment Management' };
    } else if (path.includes('/approvalPersonsInvoiceManagement')) {
      return { module: 'Invoice Management', subModule: null };
    } else if (path.includes('/approvalPersonsSystemInvoiceManagement')) {
      return { module: 'System Invoice Management', subModule: null };
    } else if (path.includes('/vendorInvoiceManagement')) {
      return { module: 'Invoice Management', subModule: null };
    } else if (path.includes('/jazzSupport')) {
      return { module: 'Jazz Support', subModule: null };
    } else if (path.includes('/loginLockoutManagement')) {
      return { module: 'Login Lockout', subModule: null };
    }
    return { module: 'Home', subModule: null };
  };

  const { module: currentModule, subModule: currentSubModule } = getCurrentModule();

  // Auto-expand the current module's menu
  useEffect(() => {
    if (currentModule && currentModule !== 'Home' && currentModule !== 'Access Control') {
      setExpandedMenus((prev) => ({
        ...prev,
        [currentModule]: true,
      }));
    }
  }, [currentModule]);

  const handleMenuToggle = (menuName) => {
    setExpandedMenus((prev) => ({
      ...prev,
      [menuName]: !prev[menuName],
    }));
  };

  const handleNavigation = (moduleName, subModuleName = null) => {
    // Close drawer after navigation
    toggleDrawer(false)();

    // Navigate based on module and sub-module
    if (moduleName === 'Home') {
      navigate('/rov');
    } else if (moduleName === 'Access Control') {
      if (hasAnyPermission(permissionNames, USER_MANAGEMENT_PERMISSIONS)) {
        navigate('/userManagement');
      }
    } else if (moduleName === 'Network Planning') {
      if (subModuleName === 'Partner Network') {
        if (hasAnyPermission(permissionNames, PARTNER_NETWORK_PERMISSIONS)) {
          navigate('/kmzMap');
        }
      } else if (subModuleName === 'Ring Planning') {
        if (hasAnyPermission(permissionNames, RING_PLANNING_PERMISSIONS)) {
          navigate('/networkPlanning/ringPlanning');
        }
      } else if (subModuleName === 'KMZ') {
        if (hasAnyPermission(permissionNames, KMZ_PERMISSIONS)) {
          navigate('/networkPlanning/kmzPlanning');
        }
      }
    } else if (moduleName === 'Network Monitoring') {
      if (hasNetworkMonitoringPermissions(permissionNames)) {
        if (subModuleName === 'Network Alarms') {
          navigate('/networkAlarms');
        } else if (subModuleName === 'FTTS') {
          sessionStorage.setItem('selectedTab', 'ftts');
          navigate('/fibers');
        } else if (subModuleName === 'MBPN') {
          sessionStorage.setItem('selectedTab', 'mpbn');
          navigate('/fibers');
        } else if (subModuleName === 'OTN') {
          sessionStorage.setItem('selectedTab', 'otn');
          navigate('/fibers');
        } else if (subModuleName === 'IPRAN') {
          sessionStorage.setItem('selectedTab', 'ipran');
          navigate('/fibers');
        } else {
          navigate('/fibers');
        }
      }
    } else if (moduleName === 'Inventory Management') {
      if (subModuleName === 'FTTS Site List') {
        navigate('/oko/fiberSite');
      } else if (subModuleName === 'RAN DB') {
        navigate('/oko/ranDb');
      } else if (subModuleName === 'OFC Inventory') {
        navigate('/inventory');
      } else if (subModuleName === 'Leased Fiber Pair') {
        navigate('/leasedFiber');
      } else if (subModuleName === 'Network Layer Visualization') {
        navigate('/networkLayer');
      } else if (subModuleName === 'Fiber Pop List') {
        if (hasAnyPermission(permissionNames, FIBER_POP_LIST_PERMISSIONS)) {
          navigate('/networkPlanning/fiberPopList');
        }
      } else {
        // Navigate to landing page if no specific sub-module
        navigate('/rov');
      }
    } else if (moduleName === 'Invoice Management') {
      if (permissionNames?.includes('Admin Invoice Management')) {
        if (subModuleName === 'Purchase Order Management') {
          navigate('/purchaseOrderManagement');
        } else if (subModuleName === 'Vendor Invoice Management') {
          navigate('/poWorkflowManagement');
        } else {
          navigate('/purchaseOrderManagement');
        }
      } else if (permissionNames?.includes('Approval Persons Invoice Management')) {
        navigate('/approvalPersonsInvoiceManagement');
      } else if (permissionNames?.includes('Vendor Invoice Management')) {
        // User has Vendor permission, navigate to their module
        navigate('/vendorInvoiceManagement');
      } else if (permissionNames?.includes('System Invoice Management Vendor')) {
        navigate('/systemInvoiceManagementVendor');
      }
    } else if (moduleName === 'System Invoice Management') {
      if (subModuleName === 'Purchase Order Management') {
        if (permissionNames?.includes('System Invoice Management')) {
          navigate('/systemPurchaseOrderManagement');
        }
      } else if (subModuleName === 'Vendor Invoice Management') {
        if (permissionNames?.includes('System Invoice Management')) {
          navigate('/systemPoWorkflowManagement');
        }
      } else if (hasApprovalPersonsSystemInvoiceManagementAccess(permissionNames)) {
        navigate('/approvalPersonsSystemInvoiceManagement');
      } else if (permissionNames?.includes('System Invoice Management')) {
        navigate('/systemPurchaseOrderManagement');
      }
    } else if (moduleName === 'Jazz Support') {
      if (permissionNames?.includes('Jazz Support')) {
        navigate('/jazzSupport');
      }
    } else if (moduleName === 'Login Lockout') {
      if (hasLoginLockoutManagementAccess(permissionNames)) {
        navigate('/loginLockoutManagement');
      }
    } else {
      // Navigate to landing page if no specific module
      navigate('/rov');
    }
  };

  // Filter menu items based on permissions
  const getFilteredMenuItems = () => {
    const items = [
      {
        name: 'Home',
        icon: <img src={homeIcon} />,
        hasSubItems: false,
        show: true, // Home is always visible
      },
      {
        name: 'Access Control',
        icon: <AccessControlLeftBart hideTooltip={true} />,
        hasSubItems: false,
        show: hasAnyPermission(permissionNames, USER_MANAGEMENT_PERMISSIONS),
      },
      {
        name: 'Login Lockout',
        icon: <AccessControlLeftBart hideTooltip={true} />,
        hasSubItems: false,
        show: hasLoginLockoutManagementAccess(permissionNames),
      },
      {
        name: 'Network Planning',
        icon: <PartnerNetworkLeftBarPath hideTooltip={true} />,
        hasSubItems: true,
        subItems: [
          {
            name: 'Partner Network',
            displayName: 'Comprehensive Route Planning',
            show: hasAnyPermission(permissionNames, PARTNER_NETWORK_PERMISSIONS),
          },
          {
            name: 'KMZ',
            displayName: 'KMZ Upload with Network Overlay',
            show: hasAnyPermission(permissionNames, KMZ_PERMISSIONS),
          },
          {
            name: 'Ring Planning',
            show: hasAnyPermission(permissionNames, RING_PLANNING_PERMISSIONS),
          },
        ],
        show: hasAnyPermission(
          permissionNames,
          [PARTNER_NETWORK_PERMISSIONS, KMZ_PERMISSIONS, RING_PLANNING_PERMISSIONS].flat()
        ),
      },
      {
        name: 'Network Monitoring',
        icon: <NetworkMonitoringLeftBarPath hideTooltip={true} />,
        hasSubItems: true,
        subItems: (() => {
          const availablePermissions = getAvailableNetworkMonitoringPermissions(permissionNames);
          return availablePermissions.map((permission) => ({
            name: permission,
            show: true,
          }));
        })(),
        show: hasNetworkMonitoringPermissions(permissionNames),
      },
      {
        name: 'Inventory Management',
        icon: <img src={inventory} style={{ width: '20px', height: '20px' }} />,
        hasSubItems: true,
        subItems: [
          {
            name: 'FTTS Site List',
            show: hasAnyPermission(permissionNames, ['fiberSite']),
          },
          {
            name: 'RAN DB',
            show: hasAnyPermission(permissionNames, ['ranDb']),
          },
          {
            name: 'OFC Inventory',
            show: hasAnyPermission(permissionNames, ['Inventory']),
          },
          {
            name: 'Leased Fiber Pair',
            show: hasAnyPermission(permissionNames, ['Leased Fiber Pair']),
          },
          {
            name: 'Network Layer Visualization',
            show: hasAnyPermission(permissionNames, ['Network Layer Visualization']),
          },
          {
            name: 'Fiber Pop List',
            show: hasAnyPermission(permissionNames, FIBER_POP_LIST_PERMISSIONS),
          },
        ],
        show: (() => {
          // Only show if user has at least one inventory permission
          const inventoryPermissions = [
            'FTTS Site List',
            'RAN DB',
            'Inventory',
            'Leased Fiber Pair',
            'Network Layer Visualization',
            'Fiber Pop List',
          ];
          return hasAnyPermission(permissionNames, inventoryPermissions);
        })(),
      },
      {
        name: 'Invoice Management',
        icon: <InvoiceManagementLeftBarPath hideTooltip={true} />,
        hasSubItems: permissionNames?.includes('Admin Invoice Management'),
        subItems: [
          {
            name: 'Purchase Order Management',
            show: permissionNames?.includes('Admin Invoice Management'),
          },
          {
            name: 'Vendor Invoice Management',
            show: permissionNames?.includes('Admin Invoice Management'),
          },
        ],
        show:
          permissionNames?.includes('Admin Invoice Management') ||
          permissionNames?.includes('Approval Persons Invoice Management') ||
          permissionNames?.includes('Vendor Invoice Management') ||
          permissionNames?.includes('System Invoice Management Vendor'),
      },
      {
        name: 'System Invoice Management',
        icon: <InvoiceManagementLeftBarPath hideTooltip={true} />,
        hasSubItems: permissionNames?.includes('System Invoice Management'),
        subItems: [
          {
            name: 'Purchase Order Management',
            show: permissionNames?.includes('System Invoice Management'),
          },
          {
            name: 'Vendor Invoice Management',
            show: permissionNames?.includes('System Invoice Management'),
          },
        ],
        show:
          permissionNames?.includes('System Invoice Management') ||
          hasApprovalPersonsSystemInvoiceManagementAccess(permissionNames),
      },
      {
        name: 'Jazz Support',
        icon: <img src={ticketIcon} style={{ width: '20px', height: '20px' }} />,
        hasSubItems: false,
        show: permissionNames?.includes('Jazz Support'),
      },
    ];

    return items.filter((item) => item.show);
  };

  const menuItems = getFilteredMenuItems();

  const DrawerList = (
    <Box sx={{ width: 280 }} role="presentation">
      {/* Header with Logo and Brand */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          p: 1.2,
          background: '#192126',
          borderBottom: '1px solid rgb(49, 47, 47)',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <img
            alt=""
            src={environment === 'demo' ? okoLogo : logo}
            style={{ height: '50px', cursor: 'pointer' }}
            onClick={() => navigate('/rov')}
          />
          {/* Brand Name */}
          <Box sx={{ fontWeight: 'bold', fontSize: '16px', color: 'white' }}>JAZZ ROV</Box>
        </Box>
        {/* Collapse Button */}
        <IconButton
          onClick={toggleDrawer(false)}
          sx={{
            transition: 'all 0.2s ease',
            '&:hover': {
              backgroundColor: 'rgba(255, 255, 255, 0.1)',
              transform: 'scale(1.1)',
            },
            '&:active': {
              transform: 'scale(0.95)',
            },
          }}
        >
          <img src={circleBackArrow} />
        </IconButton>
      </Box>

      {/* Navigation Items */}
      <List sx={{ pt: 3.5 }}>
        {menuItems.map((item) => {
          const isCurrentModule = currentModule === item.name;
          const isCurrentSubModule = item.subItems?.some(
            (subItem) => subItem.name === currentSubModule
          );

          return (
            <React.Fragment key={item.name}>
              <ListItem sx={{ pt: 1, pl: 0 }}>
                <ListItemButton
                  sx={{
                    backgroundColor: isCurrentModule ? 'rgba(189, 29, 35, 0.1)' : 'transparent',
                    pl: '2px',
                    pr: '4px',
                    width: '100%',
                    borderRadius: '4px',
                    '&:hover': {
                      backgroundColor: isCurrentModule
                        ? 'rgba(189, 29, 35, 0.2)'
                        : 'rgba(0, 0, 0, 0.04)',
                    },
                  }}
                  onClick={() => {
                    if (item.hasSubItems) {
                      handleMenuToggle(item.name);
                    } else {
                      handleNavigation(item.name);
                    }
                  }}
                >
                  <ListItemIcon
                    sx={{
                      minWidth: 32,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    {item.icon}
                  </ListItemIcon>
                  <ListItemText
                    primary={item.name}
                    sx={{
                      '& .MuiListItemText-primary': {
                        fontSize: '14px',
                        fontWeight: isCurrentModule ? 600 : 400,
                        color: isCurrentModule ? '#bd1d23' : 'inherit',
                      },
                      pl: '0px',
                      flex: 1,
                    }}
                  />
                  {item.hasSubItems && (
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        ml: 'auto',
                        mr: 2,
                      }}
                    >
                      {expandedMenus[item.name] ? <ExpandLess /> : <ExpandMore />}
                    </Box>
                  )}
                </ListItemButton>
              </ListItem>

              {/* Sub-items */}
              {item.hasSubItems && (
                <Collapse in={expandedMenus[item.name]} timeout="auto" unmountOnExit>
                  <List component="div" disablePadding>
                    {item.subItems
                      .filter((subItem) => subItem.show)
                      .map((subItem) => {
                        const isCurrentSubItem = currentSubModule === subItem.name;

                        return (
                          <ListItem key={subItem.name} disablePadding>
                            <ListItemButton
                              sx={{
                                backgroundColor: isCurrentSubItem
                                  ? 'rgba(189, 29, 35, 0.1)'
                                  : 'transparent',
                                '&:hover': {
                                  backgroundColor: isCurrentSubItem
                                    ? 'rgba(189, 29, 35, 0.2)'
                                    : 'rgba(0, 0, 0, 0.04)',
                                },
                              }}
                              onClick={() => handleNavigation(item.name, subItem.name)}
                            >
                              <ListItemText
                                primary={`— ${subItem.displayName ?? subItem.name}`}
                                sx={{
                                  '& .MuiListItemText-primary': {
                                    fontSize: '13px',
                                    color: isCurrentSubItem ? '#bd1d23' : '#666',
                                    fontWeight: isCurrentSubItem ? 600 : 400,
                                  },
                                }}
                              />
                            </ListItemButton>
                          </ListItem>
                        );
                      })}
                  </List>
                </Collapse>
              )}
            </React.Fragment>
          );
        })}
      </List>
    </Box>
  );

  return (
    <div>
      <Drawer
        open={open}
        onClose={toggleDrawer(false)}
        sx={{
          '& .MuiDrawer-paper': {
            backgroundColor: 'white',
            boxShadow: '2px 0 8px rgba(0,0,0,0.1)',
          },
        }}
      >
        {DrawerList}
      </Drawer>
    </div>
  );
}
